import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../home/Navbar';
import Footer from '../home/Footer';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';
import ReactToPdf from 'react-to-pdf';
import {
  TbZodiacAries, TbZodiacTaurus, TbZodiacGemini, TbZodiacCancer, TbZodiacLeo,
  TbZodiacVirgo, TbZodiacLibra, TbZodiacScorpio, TbZodiacSagittarius, TbZodiacCapricorn,
  TbZodiacAquarius, TbZodiacPisces
} from "react-icons/tb";
import Queryform from './Queryform';
import { PDFDownloadLink } from '@react-pdf/renderer';
import ChartPdf from './ChartPdf';


const NatalChartResult = () => {
  const location = useLocation();
  const { data } = location.state || {};

  const natalChartRef = useRef(null);

  const [resolvedPlanets, setResolvedPlanets] = useState([]);
  const [resolvedHouses, setResolvedHouses] = useState([]);
  const [chartPngBase64, setChartPngBase64] = useState(null);
  const [loading, setLoading] = useState(true);

  const planetsData = data?.planetsData || { output: [] };
  const housesData = data?.housesData || { output: { Houses: [] } };
  const chartImage = data?.chartImage || '';

  const isValidImage = (url) => {
    return (
      url &&
      typeof url === 'string' &&
      url.startsWith('https://western-astrology.s3.ap-south-1.amazonaws.com/') &&
      url.endsWith('.svg')
    );
  };

  const getZodiacIcon = (sign) => {
    const zodiacIcons = {
      Aries: <TbZodiacAries className="inline text-red-500" />,
      Taurus: <TbZodiacTaurus className="inline text-green-500" />,
      Gemini: <TbZodiacGemini className="inline text-yellow-500" />,
      Cancer: <TbZodiacCancer className="inline text-blue-500" />,
      Leo: <TbZodiacLeo className="inline text-orange-500" />,
      Virgo: <TbZodiacVirgo className="inline text-green-700" />,
      Libra: <TbZodiacLibra className="inline text-pink-500" />,
      Scorpio: <TbZodiacScorpio className="inline text-purple-500" />,
      Sagittarius: <TbZodiacSagittarius className="inline text-indigo-500" />,
      Capricorn: <TbZodiacCapricorn className="inline text-gray-600" />,
      Aquarius: <TbZodiacAquarius className="inline text-blue-400" />,
      Pisces: <TbZodiacPisces className="inline text-teal-500" />,
    };
    return zodiacIcons[sign] || null;
  };

  const resolveAsyncData = async () => {
    try {
      const resolvedPlanetData = (planetsData.output || []).map((planet) => {
        const zodiacName = planet.zodiac_sign?.name?.en;
        return {
          ...planet,
          zodiac_sign: {
            ...planet.zodiac_sign,
            name: { en: zodiacName },
          },
        };
      });

      const resolvedHouseData = (housesData.output?.Houses || []).map((house) => {
        const zodiacName = house.zodiac_sign?.name?.en;
        return {
          ...house,
          zodiac_sign: {
            ...house.zodiac_sign,
            name: { en: zodiacName },
          },
        };
      });

      setResolvedPlanets(resolvedPlanetData);
      setResolvedHouses(resolvedHouseData);
      setLoading(false);
    } catch (error) {
      console.error('Error resolving data:', error);
    }
  };

  const convertSvgToPngBase64 = (svgUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width || 500;
        canvas.height = img.height || 500;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const pngDataUrl = canvas.toDataURL('image/png');
        resolve(pngDataUrl);
      };
      img.onerror = reject;
      img.src = svgUrl;
    });
  };

  useEffect(() => {
    resolveAsyncData();
  }, [planetsData, housesData]);

  useEffect(() => {
    if (chartImage && chartImage.endsWith('.svg')) {
      convertSvgToPngBase64(chartImage)
        .then(setChartPngBase64)
        .catch((err) => console.error('SVG to PNG conversion failed', err));
    } else {
      setChartPngBase64(chartImage);
    }
  }, [chartImage]);

  if (loading) {
    return <div className="text-center py-10">Loading chart...</div>;
  }


  return (
    <section className="min-h-screen bg-gradient-to-r from-white to-success/40 text-gray-900">
      <Navbar />

      <main className="text-gray-900 py-12 px-6" ref={natalChartRef}>

        {/* Title */}
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-secondary">Your Natal Chart Report</h1>
        </div>

        <div className="flex flex-col items-center gap-10 w-full max-w-5xl mx-auto">
          {/* Chart Image */}
          <div className="text-center mt-4">
            <h2 className="text-lg sm:text-xl md:text-2xl font-semibold bg-secondary text-white p-2 rounded-full font-serif uppercase mb-4">Natal Chart</h2>
            {isValidImage(chartImage) ? (
              <img
                src={chartImage}
                alt="Natal Chart"
                className="mx-auto max-w-full lg:max-w-lg h-auto rounded-full shadow-lg border-2 border-gray-300"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'path/to/fallback-image.png';
                  console.error('Image failed to load', chartImage);
                }}
              />
            ) : (
              <p className="text-gray-500 text-lg font-semibold">No chart image available</p>
            )}
          </div>

          {/* Planets Info */}
          <div className="bg-gradient-to-bl from-slate-50 to-gray-50 shadow-lg rounded-lg p-6 w-full border-t-4 border-secondary">
            <h2 className="text-lg sm:text-xl md:text-2xl font-serif font-semibold text-center text-indigo-600 uppercase mb-4">Planets Information</h2>
            <div className="overflow-x-auto">
              <Table className="w-full border-collapse text-left">
                <Thead>
                  <Tr className="bg-indigo-500 text-white">
                    <Th className="px-4 py-2">Planet</Th>
                    <Th className="px-4 py-2">Full Degree</Th>
                    <Th className="px-4 py-2">Norm Degree</Th>
                    <Th className="px-4 py-2">Is Retro</Th>
                    <Th className="px-4 py-2">Zodiac Sign</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {resolvedPlanets.map((planet, index) => (
                    <Tr key={index} className="border-b hover:bg-gray-100">
                      <Td className="px-4 py-2">{planet.planet.en}</Td>
                      <Td className="px-4 py-2">{planet.fullDegree}</Td>
                      <Td className="px-4 py-2">{planet.normDegree}</Td>
                      <Td className="px-4 py-2">{planet.isRetro ? 'Yes' : 'No'}</Td>
                      <Td className="px-4 py-2 flex items-center">
                        {getZodiacIcon(planet.zodiac_sign.name.en)}
                        <div className="ml-2">{planet.zodiac_sign.name.en}</div>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </div>
          </div>

          {/* Houses Info */}
          <div className="bg-white shadow-lg rounded-lg p-6 w-full border-t-4 border-green-500">
            <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-green-600 uppercase mb-4">Houses Information</h2>
            <div className="overflow-x-auto">
              <Table className="w-full border-collapse text-left">
                <Thead>
                  <Tr className="bg-green-500 text-white">
                    <Th className="px-4 py-2">House</Th>
                    <Th className="px-4 py-2">Degree</Th>
                    <Th className="px-4 py-2">Norm Degree</Th>
                    <Th className="px-4 py-2">Zodiac Sign</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {resolvedHouses.map((house, index) => (
                    <Tr key={index} className="border-b hover:bg-gray-100">
                      <Td className="px-4 py-2">{house.House}</Td>
                      <Td className="px-4 py-2">{house.degree}</Td>
                      <Td className="px-4 py-2">{house.normDegree}</Td>
                      <Td className="px-4 py-2 flex items-center">
                        {getZodiacIcon(house.zodiac_sign.name.en)}
                        <div className="ml-2">{house.zodiac_sign.name.en}</div>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </div>
          </div>
        </div>
      </main>

      {/* <div className="text-center mt-6">
        <PDFDownloadLink
          document={
            <ChartPdf
              resolvedPlanets={resolvedPlanets}
              resolvedHouses={resolvedHouses}
              chartImage={chartPngBase64 || ''}
            />
          }
          fileName="natal-chart-report.pdf"
        >
          {({ loading }) =>
            loading ? (
              <button className="bg-gray-400 text-white py-2 px-6 rounded shadow">Preparing PDF...</button>
            ) : (
              <button className="bg-gray-900 hover:bg-secondary text-white font-semibold py-2 px-6 rounded shadow">
                📄 Download PDF Report
              </button>
            )
          }
        </PDFDownloadLink>


      </div> */}


      <Queryform />
      <Footer />
    </section>
  );
};

export default NatalChartResult;
